/**
 * Build-time configuration loaded from Vite env vars.
 *
 * Each consuming app (apps/main, apps/portal, apps/account, apps/vendor)
 * sets these in its own .env file at build time. The auth-client validates
 * at module load so misconfiguration fails loudly, not at first sign-in.
 */
import type { AuthAudience } from "./types.js";

/**
 * Vite injects `import.meta.env`. We type it loosely here so the package can
 * compile standalone without depending on the consumer's vite-env.d.ts.
 */
interface ViteEnv {
  VITE_AUTH_BASE_URL?: string;
  VITE_COGNITO_USER_POOL_ID?: string;
  VITE_COGNITO_APP_CLIENT_ID?: string;
  VITE_COGNITO_REGION?: string;
  VITE_AUTH_CLIENT_AUDIENCE?: string;
  VITE_AUTH_POST_LOGOUT_URL?: string;
  VITE_AUTH_REDIRECT_URI?: string;
}

// `import.meta.env` is typed by `vite/client` in Vite-based apps. We avoid
// re-declaring `ImportMeta.env` here to prevent type-collision with that
// (and with React Native / Node consumers). `readEnv()` casts at the boundary.

export interface AuthConfig {
  /** OIDC issuer base. Defaults to https://auth.molaris.ai. */
  authBaseUrl: string;
  /** Cognito user-pool ID (informational; auth service uses it server-side). */
  cognitoUserPoolId: string;
  /** OAuth client ID registered with the auth service. */
  cognitoAppClientId: string;
  /** AWS region the Cognito pool lives in. */
  cognitoRegion: string;
  /** Audience this app is requesting (drives token scoping). */
  audience: AuthAudience;
  /** Where the OIDC callback lands. Defaults to `${origin}/auth/callback`. */
  redirectUri: string;
  /** Where signOut() redirects after server logout. Defaults to `${origin}/`. */
  postLogoutUri: string;
}

const VALID_AUDIENCES: readonly AuthAudience[] = [
  "clearpath-main",
  "clearpath-portal",
  // clearpath-light — the storefront/intake SPA (ENG-1046). Internal-staff
  // actor on the SAME staff Cognito pool as clearpath-main, narrowed to a
  // site-scoped Intake/Storefront Operator role. The server-side registration
  // of this audience (auth-authorizer deriveUserType/jwt_verify allowlist +
  // auth-service OIDC client + the Cognito app-client) is a separate,
  // security-auditor-reviewed change tracked alongside the infra work — see
  // the ENG-1046 PR body. Declaring it here lets the SPA construct its auth
  // client following the established per-app pattern.
  "clearpath-light",
  "clearpath-vendor",
  "molaris-admin",
  "molaris-account",
] as const;

function readEnv(): ViteEnv {
  // import.meta.env is undefined under Node (e.g. vitest run without
  // vite plugin). Fall back to process.env so unit tests can stub.
  if (typeof import.meta !== "undefined" && (import.meta as unknown as { env?: unknown }).env) {
    return (import.meta as unknown as { env: ViteEnv }).env;
  }
  if (typeof process !== "undefined" && process.env) {
    return process.env as ViteEnv;
  }
  return {};
}

export function safeOrigin(): string {
  // ENG-2907 audit B1 — must NEVER throw. `window.location.origin` can throw in a
  // hardened/sandboxed context (or from a test double), and safeOrigin() runs at
  // module init via loadAuthConfig's redirectUri/postLogoutUri defaults and via
  // sanitizePostLogoutUri — a throw there is a white-screen-at-boot in all six
  // apps. Swallow the read failure and fall back to the safe default.
  try {
    if (typeof window !== "undefined" && window.location) {
      return window.location.origin;
    }
  } catch {
    // hardened context / throwing origin getter — fall through to the default.
  }
  // SSR / test fallback. The consumer should set the env vars explicitly
  // when running outside the browser anyway.
  return "http://localhost";
}

/**
 * ENG-2907 — a `console.warn` that can never throw into the caller.
 *
 * The sign-out path is contractually never-throws (see sign-out.ts, which pins
 * its guarantee "BY CONSTRUCTION… not by hand-placed guards per property"). On
 * such a path anything added purely to OBSERVE a failure must itself be
 * incapable of causing one. `console.warn` is patched to throw by real
 * middleware (Sentry `CaptureConsole`, Datadog RUM, LogRocket, FullStory) and
 * DELIBERATELY by fail-on-console test setups, so an unguarded warn — one of
 * them sits ahead of the sign-out backstop wipe — could reject signOut(), skip
 * the wipe, and skip the fallback navigation. Route every such log through here.
 * Lives here (not client.ts) so `sanitizePostLogoutUri` can share it without a
 * client.ts <-> config.ts import cycle; client.ts imports it.
 */
export function safeWarn(...args: unknown[]): void {
  // A patched console must never break the flow it is only observing.
  try { console.warn(...args); } catch { /* swallow */ }
}

/**
 * ENG-2907 — resolve `uri` to an absolute http(s) URL for `signOut()`
 * navigation, NON-FATALLY. `signOut()` navigates with `window.location.assign`,
 * which RESOLVES a relative URL against the document base — it does NOT throw on
 * one — so the only values worth rejecting are those that do not resolve to an
 * http(s) destination at all. This must NEVER throw: `createAuthClient` runs at
 * module top level in every app (`export const authClient = createAuthClient(…)`),
 * so a throw here is a white-screen-at-boot with no auth and no recovery UI — a
 * value like `VITE_AUTH_POST_LOGOUT_URL=${UNSET_VAR}` (an empty string) must not
 * be able to brick the app. Parse against this origin (matching what `assign`
 * does), require an http/https protocol (rejecting e.g. `javascript:` or
 * `data:`), and on any failure warn and fall back to the safe `${origin}/`
 * default that loadAuthConfig itself uses. Extracted from client.ts (T8-planned)
 * so it lives beside `safeOrigin`/`postLogoutUri`.
 */
export function sanitizePostLogoutUri(uri: string): string {
  // ENG-2907 audit B2 — this function must NEVER throw (its whole reason to
  // exist; a throw here is a white-screen-at-boot). `uri` is typed `string`, but
  // an untyped-JS caller could pass a value whose `toString()` throws, and BOTH
  // the `new URL(uri, …)` coercion in the try AND the warning interpolation in
  // the catch call it. Coerce ONCE here, guarded, and interpolate this
  // pre-coerced value below so a throwing `toString()` cannot escape the catch.
  let shownUri: string;
  try {
    shownUri = String(uri);
  } catch {
    shownUri = "<unstringifiable postLogoutUri>";
  }
  try {
    const resolved = new URL(uri, safeOrigin());
    if (resolved.protocol !== "http:" && resolved.protocol !== "https:") throw new Error(resolved.protocol);
    return resolved.toString();
  } catch (err) {
    // Compute the fallback inside the catch, not before the try: safeOrigin()
    // never throws now (B1), but keeping the origin reads inside guarded scopes
    // keeps this function's never-throws guarantee local and self-evident.
    const fallback = `${safeOrigin()}/`;
    safeWarn(`[@clearpath/auth-client] postLogoutUri "${shownUri}" is not a usable http(s) URL; falling back to ${fallback}.`, err);
    return fallback;
  }
}

function isAudience(value: string | undefined): value is AuthAudience {
  return typeof value === "string" && (VALID_AUDIENCES as readonly string[]).includes(value);
}

let cached: AuthConfig | null = null;

/**
 * Load + validate auth-client config. Throws on missing required fields so
 * misconfiguration surfaces at module import, not at first sign-in attempt.
 *
 * Memoised: subsequent calls with NO overrides return the same object.
 *
 * The env source is fixed to `readEnv()` (production) and is deliberately NOT a
 * parameter of this PUBLIC function (it is re-exported from index.ts). A
 * caller-supplied env would be memoised below and then served to every later
 * default caller, so one hostile call — `loadAuthConfig({}, { VITE_AUTH_BASE_URL:
 * "https://evil.example", … })` — could install an attacker `authBaseUrl`
 * process-wide, and `apps/vendor` fetches `/account/mfa` off `authBaseUrl` with a
 * Bearer access token (ENG-2907 audit finding D). Tests that must drive the
 * env-sourced branches use `__loadAuthConfigForTests`, which never touches this
 * memo, so finding D is unreachable by construction.
 */
export function loadAuthConfig(overrides: Partial<AuthConfig> = {}): AuthConfig {
  if (cached && Object.keys(overrides).length === 0) return cached;
  const config = computeAuthConfig(overrides, readEnv());
  if (Object.keys(overrides).length === 0) cached = config;
  return config;
}

/**
 * Test-only seam: build a config from an INJECTED `env`, bypassing the memo
 * entirely. NOT re-exported from index.ts, so a caller-supplied env can never
 * reach the package's public surface nor the shared cache default callers read.
 * `readEnv()` returns the vite-injected `import.meta.env`, a per-module object a
 * test cannot mutate (and `vi.stubEnv` only touches `process.env`, which
 * `readEnv` skips while `import.meta.env` is truthy), so injecting `env` here is
 * the only way to exercise the `VITE_*` branches.
 */
export function __loadAuthConfigForTests(
  overrides: Partial<AuthConfig>,
  env: ViteEnv,
): AuthConfig {
  return computeAuthConfig(overrides, env);
}

function computeAuthConfig(overrides: Partial<AuthConfig>, env: ViteEnv): AuthConfig {
  const missing: string[] = [];

  const cognitoUserPoolId = overrides.cognitoUserPoolId ?? env.VITE_COGNITO_USER_POOL_ID;
  if (!cognitoUserPoolId) missing.push("VITE_COGNITO_USER_POOL_ID");

  const cognitoAppClientId = overrides.cognitoAppClientId ?? env.VITE_COGNITO_APP_CLIENT_ID;
  if (!cognitoAppClientId) missing.push("VITE_COGNITO_APP_CLIENT_ID");

  const cognitoRegion = overrides.cognitoRegion ?? env.VITE_COGNITO_REGION;
  if (!cognitoRegion) missing.push("VITE_COGNITO_REGION");

  const audienceRaw = overrides.audience ?? env.VITE_AUTH_CLIENT_AUDIENCE;
  if (!audienceRaw) missing.push("VITE_AUTH_CLIENT_AUDIENCE");

  if (missing.length > 0) {
    throw new Error(
      `[@clearpath/auth-client] Missing required env vars: ${missing.join(", ")}. ` +
        `Set them in the consuming app's .env file before importing the auth client.`,
    );
  }

  if (!isAudience(audienceRaw)) {
    throw new Error(
      `[@clearpath/auth-client] VITE_AUTH_CLIENT_AUDIENCE="${audienceRaw}" is not one of: ` +
        VALID_AUDIENCES.join(", "),
    );
  }

  const config: AuthConfig = {
    authBaseUrl:
      overrides.authBaseUrl ?? env.VITE_AUTH_BASE_URL ?? "https://auth.molaris.ai",
    cognitoUserPoolId: cognitoUserPoolId as string,
    cognitoAppClientId: cognitoAppClientId as string,
    cognitoRegion: cognitoRegion as string,
    audience: audienceRaw,
    redirectUri:
      overrides.redirectUri ?? env.VITE_AUTH_REDIRECT_URI ?? `${safeOrigin()}/auth/callback`,
    // ENG-2907 — an EMPTY postLogoutUri from ANY source must be treated as UNSET
    // so `??` reaches the documented `${origin}/` default rather than surviving as
    // "" (which `??` keeps, since "" is not nullish). The motivating case is a
    // deploy that interpolates an unset variable — `VITE_AUTH_POST_LOGOUT_URL=""` —
    // but an explicit "" override is coerced the same way: an empty string is
    // never a valid post-logout destination, whatever its source.
    postLogoutUri:
      (overrides.postLogoutUri || undefined) ??
      (env.VITE_AUTH_POST_LOGOUT_URL || undefined) ??
      `${safeOrigin()}/`,
  };

  return config;
}

/** Test-only: reset the memoised config so tests can re-load with new env. */
export function __resetAuthConfigForTests(): void {
  cached = null;
}
